import React, { useState, Children } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, ShoppingCart, Lock, CheckCircle2 } from 'lucide-react';
import { Logo } from '../../Logo';
import { useCart } from '../../../contexts/CartContext';
import { usePrice } from '../../../contexts/PriceContext';
import { subscribeToNewsletter } from '../../../utils/firebase';
import { useCatalogProducts } from '../../../utils/useCatalogProducts';
import { bookData, type Book } from './bookData';
const fadeUpVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.2, 0.65, 0.3, 0.9]
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const books: Book[] = bookData;
function BookCover({
  book,
  size = 'large'
}: {
  book: Book;
  size?: 'large' | 'small';
}) {
  const variantClasses: Record<Book['coverVariant'], string> = {
    charcoal: 'bg-charcoal',
    burgundy: 'bg-burgundy',
    navyslate: 'bg-navyslate'
  };
  const padding = size === 'large' ? 'p-8 md:p-10' : 'p-6';
  const titleSize = size === 'large' ? 'text-3xl md:text-4xl' : 'text-xl';
  const subtitleSize = size === 'large' ? 'text-xs' : 'text-[10px]';
  return <div className="relative w-full aspect-[3/4] group" data-id="element-2235" data-mp="16k8xfg977qek">
      <div className={`absolute inset-0 ${variantClasses[book.coverVariant]} shadow-2xl ${book.coverImage ? '' : padding} flex flex-col border border-darkgray overflow-hidden transition-transform duration-500 group-hover:scale-[1.02]`} data-id="element-2236" data-mp="16kqzwumgf0ux">
        {book.coverImage ? <img src={book.coverImage} alt={book.title} className="absolute inset-0 w-full h-full object-cover" data-id="element-2237" data-mp="1biaoancv8u0l" /> : <>
            {/* Top accent bar */}
            <div className="absolute top-0 left-0 w-full h-1.5 bg-tealslate" data-id="element-2238" data-mp="iueld0wpe5vd" />

            <Logo className={`${size === 'large' ? 'w-12 h-12' : 'w-8 h-8'} mb-auto opacity-80`} data-id="element-2239" data-mp="18i0sb8b30a7s" />

            <div className="mt-auto" data-id="element-2240" data-mp="1ciayakyvj0mn">
              <p className={`${subtitleSize} font-bold text-[#9A9A90] uppercase tracking-[0.25em] mb-3`} data-id="element-2241" data-mp="1halxtmppckeo">
                Structured Nutrition
              </p>
              <h3 className={`${titleSize} font-serif text-white leading-tight mb-3`} data-id="element-2242" data-mp="pk7om3smvw9c">
                {book.title}
              </h3>
              <div className="w-8 h-0.5 bg-tealslate mb-3" data-id="element-2243" data-mp="tnm6l004umpf" />
              <p className={`${subtitleSize} text-[#D8D8D0] uppercase tracking-widest`} data-id="element-2244" data-mp="yjl0f348cwx8">
                Jack Achenbach, MS, RDN
              </p>
            </div>
          </>}

        {/* Coming Soon Lock Overlay */}
        {book.status === 'coming-soon' && <div className="absolute inset-0 bg-charcoal/70 backdrop-blur-sm flex items-center justify-center" data-id="element-2245" data-mp="2exte0870vvwq">
            <div className="text-center" data-id="element-2246" data-mp="3a7rpepkjkmz">
              <Lock className="w-8 h-8 text-tealslate mx-auto mb-3" data-id="element-2247" data-mp="3uhty3f3oa17j" />
              <p className="text-[10px] font-bold text-white uppercase tracking-[0.25em]" data-id="element-2248" data-mp="3uiltopx9ryxt">
                Coming Soon
              </p>
            </div>
          </div>}
      </div>
      {/* Page edge depth */}
      <div className="absolute -right-2 top-2 bottom-2 w-2 bg-darkgray transform skew-y-[45deg] origin-top-left -z-10" data-id="element-2249" data-mp="3kq6bpslz3acx" />
    </div>;
}
function formatCents(cents: number): string {
  return '$' + (cents / 100).toFixed(2);
}
type NewsletterStatus = 'idle' | 'submitting' | 'success' | 'error';
export function Books() {
  const catalogProducts = useCatalogProducts();
  const guideCatalogProduct = catalogProducts?.find(product => product.id === 'glp1-nutrition-guide');
  const guideVisible = guideCatalogProduct?.visible !== false;
  const featuredBook: Book = {
    ...books[0],
    title: guideCatalogProduct?.name ?? books[0].title,
    coverImage: books[0].coverImage,
    priceCents: guideCatalogProduct?.price ?? books[0].priceCents
  };
  const {
    addItem
  } = useCart();
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState<NewsletterStatus>('idle');
  const [newsletterError, setNewsletterError] = useState('');
  const handleNewsletterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setNewsletterStatus('submitting');
    setNewsletterError('');
    try {
      await subscribeToNewsletter(newsletterEmail, 'books_page');
      setNewsletterStatus('success');
      setNewsletterEmail('');
    } catch (err) {
      setNewsletterError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
      setNewsletterStatus('error');
    }
  };
  const {
    cents,
    isLive,
    loading
  } = usePrice(featuredBook.lookupKey, featuredBook.priceCents);
  // Buyable if it has either a price ID or a lookup key
  const hasStripeRef = Boolean(featuredBook.stripePriceId || featuredBook.lookupKey);
  const canBuy = cents !== null && hasStripeRef;
  const handleAddFeatured = () => {
    if (!canBuy || cents === null) return;
    addItem({
      productId: featuredBook.id,
      name: featuredBook.title,
      unitPrice: cents,
      type: 'book',
      stripePriceId: featuredBook.stripePriceId,
      lookupKey: featuredBook.lookupKey
    });
  };
  return <div className="w-full min-h-screen bg-bodybg" data-id="element-2250" data-mp="5qzl3eh4mh0f">
      {/* HERO */}
      <section className="min-h-[426px] md:h-[426px] flex items-center bg-bodybg px-6 pt-[59px] border-b border-darkgray" data-id="element-2251" data-mp="jhcw5ppbebtz">
        <div className="max-w-4xl mx-auto text-center" data-id="element-2252" data-mp="ffrbvjagav9r">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="flex flex-col items-center" data-id="element-2253" data-mp="fidjfixcdarz">
            <motion.p variants={fadeUpVariants} className="text-[10px] md:text-xs font-bold text-[#9A9A90] uppercase tracking-[0.25em] mb-6" data-id="element-2254" data-mp="oel3ev8xcvgg">
              The Bookshop
            </motion.p>

            <motion.h1 variants={fadeUpVariants} className="text-4xl md:text-6xl font-serif text-white leading-tight mb-8" data-id="element-2255" data-mp="3r9lwd6gbqoy0">
              Books for building structure.
            </motion.h1>

            <motion.div variants={fadeUpVariants} className="w-16 h-0.5 bg-tealslate mb-8" data-id="element-2256" data-mp="4szk63jygcc9" />

            <motion.p variants={fadeUpVariants} className="text-base md:text-lg text-[#D8D8D0] max-w-2xl leading-relaxed" data-id="element-2257" data-mp="9uyn5cbs978o">
              Comprehensive guides distilled from years of clinical practice.
              Practical frameworks for nutrition, body composition, and
              sustainable metabolic health.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* FEATURED BOOK */}
      {guideVisible ? <section className="bg-[#F9F9F9] py-20 md:py-32 px-6" data-id="element-2258" data-mp="2d2p8dwom2xsw">
        <div className="max-w-7xl mx-auto" data-id="element-2259" data-mp="28ae7orm1r7za">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-2260" data-mp="288l1fdfciprt">
            {/* Cover */}
            <motion.div variants={fadeUpVariants} className="mx-auto w-full max-w-md" data-id="element-2261" data-mp="2r1f8zman3fm0">
              <BookCover book={featuredBook} size="large" data-id="element-2262" data-mp="2m1158gg2mrts" />
            </motion.div>

            {/* Pitch */}
            <motion.div variants={fadeUpVariants} data-id="element-2263" data-mp="2hzxto6523hyd">
              <div className="flex items-center gap-3 mb-4" data-id="element-2264" data-mp="1amnqc13apmpw">
                <span className="text-[10px] font-bold text-tealslate uppercase tracking-[0.25em]" data-id="element-2265" data-mp="1ao8kvqpm15nr">
                  Featured Release
                </span>
                {featuredBook.badge && <span className="text-[10px] font-bold text-white bg-tealslate px-3 py-1 uppercase tracking-wider" data-id="element-2266" data-mp="35or5y0skton2">
                    {featuredBook.badge}
                  </span>}
              </div>

              <h2 className="text-4xl md:text-5xl font-serif text-[#1F1F1F] mb-3 leading-tight" data-id="element-2267" data-mp="31ogy0il84o5k">
                {featuredBook.title}
              </h2>
              <p className="text-xl text-tealslate font-serif italic mb-6" data-id="element-2268" data-mp="3pfcjyz300p4w">
                {featuredBook.subtitle}
              </p>
              <div className="w-12 h-0.5 bg-tealslate mb-6" data-id="element-2269" data-mp="3thhd7wpjbjr0" />

              <p className="text-lg text-[#3B3B3B] leading-relaxed mb-8" data-id="element-2270" data-mp="3fngolvsard4d">
                {featuredBook.description}
              </p>

              {/* Meta info */}
              <div className="flex items-center gap-8 mb-10 pb-8 border-b border-[#D8D8D0]" data-id="element-2271" data-mp="c6zszw5a0yn9">
                <div data-id="element-2272" data-mp="gxzr34q4tmrc">
                  <p className="text-[10px] font-bold text-[#7A7A70] uppercase tracking-widest mb-1" data-id="element-2273" data-mp="gzb31rtqe0tj">
                    Price
                  </p>
                  {cents === null ? <div className="h-8 w-24 bg-[#D8D8D0] animate-pulse rounded-sm" data-id="element-2274" data-mp="mc9hjw19zfbu" /> : <p className="text-3xl font-serif text-[#1F1F1F]" data-id="element-2275" data-mp="101tfmojwvoet">
                      {formatCents(cents)}
                      {!isLive && !loading && <span className="text-[10px] text-[#7A7A70] font-sans ml-2 normal-case tracking-normal" data-id="element-2276" data-mp="7dxk3n62w2jm">
                          · est.
                        </span>}
                    </p>}
                </div>
                <div data-id="element-2277" data-mp="hdohbwm0wvne">
                  <p className="text-[10px] font-bold text-[#7A7A70] uppercase tracking-widest mb-1" data-id="element-2278" data-mp="ha0nsxgkw98o">
                    Format
                  </p>
                  <p className="text-base text-[#3B3B3B]" data-id="element-2279" data-mp="1sq8r513u3ole">
                    {featuredBook.pages} · PDF
                  </p>
                </div>
              </div>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-4 mb-6" data-id="element-2280" data-mp="3i2inc95oisz0">
                <button onClick={handleAddFeatured} disabled={!canBuy} className="flex items-center justify-center gap-2 px-8 py-4 bg-tealslate text-white text-sm font-bold uppercase tracking-wider hover:bg-navyslate transition-colors disabled:opacity-60 disabled:cursor-not-allowed" title={!canBuy ? 'Not yet available for purchase' : undefined} data-id="element-2281" data-mp="3d4qt38hkaurn">
                  <ShoppingCart className="w-4 h-4" data-id="element-2282" data-mp="2ud6iflhdx72i" />
                  {cents !== null ? 'Add to Cart — ' + formatCents(cents) : 'Add to Cart'}
                </button>
                <Link to="/booklet?preview=1" className="flex items-center justify-center gap-2 px-8 py-4 border border-[#1F1F1F] text-sm font-bold uppercase tracking-wider hover:bg-[#1F1F1F] hover:text-white transition-colors text-[#FFFFFF]" data-id="element-2283" data-mp="2khcw5adv5z7j">
                  <BookOpen className="w-4 h-4" data-id="element-2284" data-mp="r5pabo4i5t6k" />
                  Preview Online
                </Link>
              </div>

              <p className="text-xs text-[#7A7A70] italic" data-id="element-2285" data-mp="v69bd2s75gxz">
                Instant download · 30-day satisfaction guarantee
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section> : <section className="bg-[#F9F9F9] py-20 md:py-32 px-6" data-id="element-2286" data-mp="14w2jftt4rdxh">
          <div className="max-w-3xl mx-auto text-center" data-id="element-2287" data-mp="87d3i912cve5">
            <p className="text-[10px] font-bold text-tealslate uppercase tracking-[0.25em] mb-4" data-id="element-2288" data-mp="88xwvvbn68af">
              New releases ahead
            </p>
            <h2 className="text-3xl md:text-4xl font-serif text-[#1F1F1F] mb-5" data-id="element-2289" data-mp="h685ta0zllc6">
              The bookshop is being refreshed.
            </h2>
            <p className="text-base text-[#5A5A5A] leading-relaxed" data-id="element-2290" data-mp="m3zy455jn9gw">
              New evidence-based guides are on the way. Join the list to hear
              when the next release is available.
            </p>
          </div>
        </section>}

      {/* NEWSLETTER CTA */}
      <section className="bg-[#F9F9F9] py-20 md:py-24 px-6" data-id="element-2291" data-mp="17vj743gb5061">
        <div className="max-w-3xl mx-auto text-center" data-id="element-2292" data-mp="12z2ht5xdgfrd">
          <p className="text-[10px] font-bold text-tealslate uppercase tracking-[0.25em] mb-4" data-id="element-2293" data-mp="130nbyufrxwop">
            Stay In The Loop
          </p>
          <h2 className="text-3xl md:text-4xl font-serif text-[#1F1F1F] mb-6" data-id="element-2294" data-mp="1cw791k62r1f1">
            Be the first to know when new books drop.
          </h2>
          <p className="text-base text-[#3B3B3B] mb-10 leading-relaxed" data-id="element-2295" data-mp="p4s19bvj3rwe">
            Join the newsletter for early access, launch discounts, and
            exclusive content.
          </p>

          {newsletterStatus === 'success' ? <div className="max-w-xl mx-auto bg-white border-l-4 border-tealslate p-6 flex items-start gap-4 text-left" data-id="element-2296" data-mp="u5fr3u80u2p1">
              <CheckCircle2 className="w-6 h-6 text-tealslate shrink-0 mt-0.5" data-id="element-2297" data-mp="6fd2xp789gvp" />
              <div data-id="element-2298" data-mp="1htked3hyhxy">
                <p className="font-serif text-lg text-[#1F1F1F] mb-1" data-id="element-2299" data-mp="1gik4n99fsbl">
                  You're on the list.
                </p>
                <p className="text-sm text-[#3B3B3B]" data-id="element-2300" data-mp="2d2kegsvp443q">
                  We'll send you a note the moment new books drop.
                </p>
              </div>
            </div> : <form className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto" onSubmit={handleNewsletterSubmit} data-id="element-2301" data-mp="238akj6r3uxqw">
              <input type="email" placeholder="Enter your email" value={newsletterEmail} onChange={e => setNewsletterEmail(e.target.value)} className="flex-1 bg-white border border-[#D8D8D0] px-6 py-4 text-[#1F1F1F] focus:outline-none focus:border-tealslate transition-colors" required disabled={newsletterStatus === 'submitting'} data-id="element-2302" data-mp="2vuvca8bhtbuz" />
              <button type="submit" disabled={newsletterStatus === 'submitting'} className="px-8 py-4 bg-tealslate text-white text-sm font-bold uppercase tracking-wider hover:bg-navyslate transition-colors flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed" data-id="element-2303" data-mp="1bhnz7y5qn8bf">
                {newsletterStatus === 'submitting' ? <>
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-2304" data-mp="1gzwsh34lw8zj" />
                    Subscribing…
                  </> : <>
                    Subscribe <ArrowRight className="w-4 h-4" data-id="element-2305" data-mp="29p3zkqi4m6we" />
                  </>}
              </button>
            </form>}

          {newsletterStatus === 'error' && <p className="text-sm text-red-700 mt-4" data-id="element-2306" data-mp="20m2xd0wdszy0">{newsletterError}</p>}
        </div>
      </section>
    </div>;
}